import { Check } from 'lucide-react';
import { MIN_ORG_GOALS, ORG_GOAL_TOTAL_WEIGHTAGE } from '@cfl/shared';

import { cn } from '@/lib/utils';

/**
 * The weightage meter.
 *
 * Shows the total, what is left, and the goal count together, because the set is
 * blocked by BOTH rules and showing only one sends the manager round in circles:
 * hitting 100% with four goals still cannot be sent.
 *
 * Over 100 is destructive, exactly 100 is success, anything short is neutral - a
 * half-built draft is normal, not a warning.
 */
export function WeightageMeter({
  total,
  goalCount,
}: {
  total: number;
  goalCount: number;
}) {
  const remaining = ORG_GOAL_TOTAL_WEIGHTAGE - total;
  const over = total > ORG_GOAL_TOTAL_WEIGHTAGE;
  const complete = total === ORG_GOAL_TOTAL_WEIGHTAGE;
  const enoughGoals = goalCount >= MIN_ORG_GOALS;
  const ready = complete && enoughGoals;

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-4">
        <span className="text-xs font-medium text-muted-foreground">Total weightage</span>
        <span
          className={cn(
            'pms-num text-2xl font-bold tabular-nums',
            over && 'text-destructive',
            complete && 'text-success',
          )}
        >
          {total}
          <span className="text-sm font-medium text-muted-foreground">/{ORG_GOAL_TOTAL_WEIGHTAGE}%</span>
        </span>
      </div>

      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={cn(
            'h-full rounded-full transition-[width]',
            over ? 'bg-destructive' : complete ? 'bg-success' : 'bg-primary',
          )}
          style={{ width: `${Math.min(100, (total / ORG_GOAL_TOTAL_WEIGHTAGE) * 100)}%` }}
        />
      </div>

      <p
        className={cn(
          'flex items-center gap-1.5 text-xs',
          over ? 'text-destructive' : ready ? 'text-success' : 'text-muted-foreground',
        )}
      >
        {ready && <Check className="size-3.5" />}
        {over
          ? `${Math.abs(remaining)}% over — remove weightage from a goal`
          : ready
            ? `Ready to send · ${goalCount} goals`
            : !complete
              ? `${remaining}% remaining · ${goalCount} of ${MIN_ORG_GOALS} goals minimum`
              : `At 100%, but ${MIN_ORG_GOALS - goalCount} more goal(s) needed`}
      </p>
    </div>
  );
}
